import { useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { AlertTriangle, Check, Server, Gift, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { SUBSCRIPTION_PLANS, ONETIME_PLANS } from "@/data/plans";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_DESC = {
  "plan-filmes-series-monthly": "descMoviesSeries", "plan-filmes-monthly": "descMovies", "plan-series-monthly": "descSeries",
  "plan-filmes-series-once": "descOnce", "plan-filmes-once": "descOnce", "plan-series-once": "descOnce",
};
const PLAN_TITLE = {
  "plan-filmes-series-monthly": "titleMoviesSeries", "plan-filmes-monthly": "titleMovies", "plan-series-monthly": "titleSeries",
  "plan-filmes-series-once": "titleMoviesSeries", "plan-filmes-once": "titleMovies", "plan-series-once": "titleSeries",
};

const PlanCard = ({ plan, index }) => {
  const navigate = useNavigate();
  const t = useT();
  const featured = plan.featured;
  const isSub = plan.period.includes("30");
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className={`relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
        featured ? "border-green-500 bg-green-50/60 shadow-lg shadow-green-100" : "border-slate-200 bg-white"
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {plan.badge && (
        <span className="absolute right-3 top-3 rounded-full bg-green-600 px-2.5 py-0.5 text-[11px] font-semibold text-white">
          {featured && isSub ? t("plans.badgeMost") : t("plans.badgeBest")}
        </span>
      )}
      <div className={`px-6 py-4 text-center font-display text-lg font-bold ${featured ? "bg-green-600 text-white" : "bg-slate-50 text-slate-700"}`}>
        {t(`plans.${PLAN_TITLE[plan.id]}`)}
      </div>
      <div className="flex flex-1 flex-col px-6 py-6">
        <div className="text-center">
          <div className="font-display text-4xl font-extrabold tracking-tight text-slate-900">{plan.price}</div>
          <div className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-400">
            {isSub ? t("plans.period30") : t("plans.periodOnce")}
          </div>
        </div>
        <Button data-testid={`plan-buy-${plan.id}`} onClick={() => { const rn = new URLSearchParams(window.location.search).get("renew"); navigate(`/checkout/${plan.id}${rn ? `?renew=${rn}` : ""}`); }}
          className={`mt-5 w-full py-6 font-semibold active:scale-95 ${
            featured ? "bg-green-600 text-white hover:bg-green-700"
              : "border border-slate-300 bg-white text-slate-700 hover:border-green-500 hover:bg-green-50 hover:text-green-700"
          }`}>
          {isSub ? t("plans.ctaSub") : t("plans.ctaOnce")}
        </Button>
        <p className="mt-4 text-center text-sm leading-relaxed text-slate-500">{t(`plans.${PLAN_DESC[plan.id]}`)}</p>
        {plan.info && (
          <div className="mt-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-center text-xs leading-relaxed text-yellow-800">
            {t("plans.infoOnce")}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function Landing() {
  const t = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loadingTrial, setLoadingTrial] = useState(false);
  const renewCode = new URLSearchParams(window.location.search).get("renew");
  const startTrial = async () => {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Digite um e-mail válido."); return; }
    setLoadingTrial(true);
    try {
      const { data } = await axios.post(`${API}/trial/start`, { email: e });
      toast.success("Teste de 1 dia liberado! Abrindo seu painel…");
      navigate(`/panel/${data.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível iniciar o teste.");
    } finally { setLoadingTrial(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {renewCode ? (
          <div data-testid="renew-banner" className="mb-6 rounded-2xl border border-green-300 bg-green-50 p-4 text-center text-sm font-semibold text-green-800">
            Você está assinando para liberar o painel <span className="font-mono-code">{renewCode}</span>. Escolha um plano abaixo — seu código, configurações e IP serão mantidos.
          </div>
        ) : (
        /* Teste grátis de 1 dia */
        <div data-testid="trial-card"
          className="mb-6 flex flex-col gap-3 rounded-2xl border border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-600 text-white"><Gift className="h-5 w-5" /></span>
            <div>
              <div className="font-display text-lg font-bold text-slate-800">Teste grátis por 1 dia</div>
              <p className="text-sm text-slate-600">Ative um painel completo por 24h, sem pagar. Depois é só assinar para continuar — <b>o IP do seu servidor fica travado</b> (para trocar, fale com o suporte).</p>
            </div>
          </div>
          <div className="flex w-full shrink-0 gap-2 sm:w-auto">
            <input data-testid="trial-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com" disabled={loadingTrial}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none sm:w-56" />
            <Button data-testid="trial-start-btn" onClick={startTrial} disabled={loadingTrial}
              className="shrink-0 bg-green-600 font-semibold text-white hover:bg-green-700">
              {loadingTrial ? <Loader2 className="h-4 w-4 animate-spin" /> : "Testar 1 dia grátis"}
            </Button>
          </div>
        </div>
        )}

        <div data-testid="expiry-banner"
          className="mb-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t("landing.expiryWarn")}</span>
        </div>

        <p className="text-slate-600">{t("landing.intro")}</p>
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {t("landing.contactBefore")}
        </div>

        <div className="mt-6 flex flex-col gap-2 border-b border-slate-200 pb-4">
          <div className="flex items-center gap-2 text-slate-700">
            <Server className="h-5 w-5 text-green-600" />
            <span className="font-medium">{t("landing.supported")}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {["XUI ONE", "XTREAM UI", "STREAMCREED", "1-STREAM", "BINSTREAM", "CKMODS", "ODIN"].map((s) => (
              <span key={s} data-testid={`supported-${s}`}
                className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                {s}
              </span>
            ))}
          </div>
        </div>

        <section className="mt-8">
          <h2 className="mb-4 font-display text-xl font-bold text-slate-800">{t("landing.subscribe30")}</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {SUBSCRIPTION_PLANS.map((p, i) => <PlanCard key={p.id} plan={p} index={i} />)}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="mb-4 font-display text-xl font-bold text-slate-800">{t("landing.buyOne")}</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {ONETIME_PLANS.map((p, i) => <PlanCard key={p.id} plan={p} index={i} />)}
          </div>
        </section>

        <footer className="mt-16 flex items-center justify-center gap-1 border-t border-slate-200 py-8 text-sm text-slate-400">
          <Check className="h-4 w-4 text-green-500" /> {t("landing.footer")}
        </footer>
      </main>
    </div>
  );
}
